import {
  Box,
  BoxProps,
  Button,
  CloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import {SearchIcon} from '../../svg/SearchIcon';
import {SEARCH_MIN_CHARS, routes, useSearchClient} from 'ff-common';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import NiceModal from '@ebay/nice-modal-react';
import {SearchModal} from './SearchModal';
import {useTranslation} from 'react-i18next';
import {useSearch} from './SearchContext';
import {debounce} from 'lodash-es';
import {useLocation, useNavigate} from 'react-router-dom';
import {useClientRoute} from '@/hooks/auth/useClientRoute';

export const Searchbar: React.FC<{
  props?: BoxProps;
  alwaysShowInput?: boolean;
}> = ({props, alwaysShowInput = false}) => {
  const {t} = useTranslation();
  const search = useSearchClient();

  const handleOpenModal = useCallback(() => {
    NiceModal.show(SearchModal);
  }, []);

  // Recherche live (md+)
  const {setLive, clearLive, liveTerm} = useSearch();
  const [localTerm, setLocalTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {buildClientRoute} = useClientRoute();

  // Synchronise l'input local quand la recherche est réinitialisée depuis l'extérieur (ex: clic sur une catégorie dans la sidebar)
  useEffect(() => {
    if (!liveTerm) {
      setLocalTerm('');
    }
  }, [liveTerm]);

  // Réinitialise la recherche quand on change de page (navigation).
  // On écoute aussi location.search pour gérer le cas où on est déjà sur /catalogue
  // et qu'on clique sur une catégorie (seul le query param cat_web change).
  useEffect(() => {
    clearLive();
    setLocalTerm('');
  }, [location.pathname, location.search, clearLive]);

  // Lance la recherche Orama avec un délai de 300ms pour éviter de chercher à chaque frappe
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (search.indexIsBuilt && searchTerm.length >= SEARCH_MIN_CHARS) {
          const results = await search.searchItems({q: searchTerm});
          setLive(searchTerm, results);
        } else {
          setLive(searchTerm);
        }
      }, 300),
    [search, setLive],
  );

  // À chaque frappe : met à jour l'input local et lance la recherche debounce
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTerm = e.target.value;
      setLocalTerm(newTerm);
      if (newTerm.length === 0) {
        clearLive();
      } else {
        debouncedSearch(newTerm);
      }
    },
    [debouncedSearch, clearLive],
  );

  // Entrée : navigue vers /recherche?q=..., Escape : réinitialise la recherche
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && localTerm) {
        clearLive();
        const rechercheUrl = buildClientRoute(
          routes.client.recherche,
          {},
          {q: localTerm},
        );
        navigate(rechercheUrl);
        inputRef.current?.blur();
      }
      if (e.key === 'Escape') {
        clearLive();
        setLocalTerm('');
        inputRef.current?.blur();
      }
    },
    [localTerm, clearLive, buildClientRoute, navigate],
  );

  // Bouton croix : vide l'input et remet le focus dessus
  const handleClear = useCallback(() => {
    clearLive();
    setLocalTerm('');
    inputRef.current?.focus();
  }, [clearLive]);

  return (
    <Box
      position='relative'
      width={alwaysShowInput ? '100%' : {base: '100%', md: '30px', lg: '100%'}}
      {...props}
    >
      {/* Mode base/sm : input read-only qui ouvre la modale */}
      <Box
        display={{base: 'block', md: 'none'}}
        onClick={handleOpenModal}
      >
        <InputGroup
          zIndex={1003}
          bg='white'
          borderTopRadius='8px'
          opacity={search.indexIsBuilt ? 1 : 0.2}
        >
          <InputLeftElement
            pointerEvents='auto'
            cursor='pointer'
          >
            <SearchIcon
              color='blue.main'
              height='20px'
              width='20px'
            />
          </InputLeftElement>
          <Input
            placeholder={
              search.indexIsBuilt
                ? t('recherche.recherche_produits')
                : t('recherche.chargement_catalogue')
            }
            isDisabled={!search.indexIsBuilt}
            tabIndex={-1}
            readOnly
            cursor='pointer'
          />
        </InputGroup>
      </Box>

      {/* Mode md : bouton icône qui ouvre la modale */}
      {!alwaysShowInput && (
        <Button
          display={{base: 'none', md: 'flex', lg: 'none'}}
          variant='ghost'
          onClick={handleOpenModal}
        >
          <SearchIcon
            color='blue.main'
            height='20px'
            width='20px'
          />
        </Button>
      )}

      {/* Mode lg+ (ou alwaysShowInput) : input éditable direct dans la navbar */}
      <InputGroup
        display={
          alwaysShowInput
            ? {base: 'none', md: 'flex'}
            : {base: 'none', lg: 'flex'}
        }
        bg='white'
        borderRadius='8px'
        opacity={search.indexIsBuilt ? 1 : 0.2}
      >
        <InputLeftElement pointerEvents='none'>
          <SearchIcon
            color='blue.main'
            height='20px'
            width='20px'
          />
        </InputLeftElement>
        <Input
          ref={inputRef}
          placeholder={
            search.indexIsBuilt
              ? t('recherche.recherche_produits')
              : t('recherche.chargement_catalogue')
          }
          isDisabled={!search.indexIsBuilt}
          value={localTerm}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          _focus={{
            boxShadow: '0 0 0 3px #0072BA33',
            border: '2px solid',
            borderColor: 'blue.main',
          }}
        />
        {localTerm && (
          <InputRightElement>
            <CloseButton
              size='sm'
              bg='gray.light_bg'
              borderRadius='full'
              onClick={handleClear}
            />
          </InputRightElement>
        )}
      </InputGroup>
    </Box>
  );
};
